import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import {
    BarChart, Bar, XAxis, YAxis, ResponsiveContainer,
    CartesianGrid, Tooltip, AreaChart, Area,
    PieChart, Pie, Cell, Legend,
} from 'recharts';
import {
    aggregateBySite,
    aggregateOverTime,
    aggregateByFormType,
    getFormTypeColour,
    formatDate,
} from '../utils/occurrenceHelpers';

const ACCENT = '#CB2C84';
const GRID_STROKE = 'rgba(255,255,255,0.08)';
const AXIS_STROKE = 'rgba(255,255,255,0.15)';
const TICK_STYLE = { fill: 'white', fontSize: 10 };

const ChartTooltip = ({ active, payload, label, isDate }) => {
    if (!active || !payload || !payload.length) return null;
    return (
        <div className="occurrence-summary__tooltip">
            <div className="occurrence-summary__tooltip-label">
                {isDate ? formatDate(label) : label}
            </div>
            {payload.map((entry, i) => (
                <div key={i} className="occurrence-summary__tooltip-value">
                    {entry.name}: <strong>{entry.value}</strong>
                </div>
            ))}
        </div>
    );
};

ChartTooltip.propTypes = {
    active: PropTypes.bool,
    payload: PropTypes.array,
    label: PropTypes.string,
    isDate: PropTypes.bool,
};

const PieLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percentage }) => {
    const RADIAN = Math.PI / 180;
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    if (percentage < 5) return null;

    return (
        <text x={x} y={y} fill="white" textAnchor="middle" dominantBaseline="central"
            fontSize={11} fontWeight={600}>
            {`${percentage}%`}
        </text>
    );
};

PieLabel.propTypes = {
    cx: PropTypes.number,
    cy: PropTypes.number,
    midAngle: PropTypes.number,
    innerRadius: PropTypes.number,
    outerRadius: PropTypes.number,
    percentage: PropTypes.number,
};

const renderLegend = (props) => {
    const { payload } = props;
    return (
        <div className="occurrence-summary__legend">
            {payload.map((entry, i) => (
                <div key={i} className="occurrence-summary__legend-item">
                    <span
                        className="occurrence-summary__legend-swatch"
                        style={{ backgroundColor: entry.color }}
                    />
                    {entry.value}
                </div>
            ))}
        </div>
    );
};

const OccurrenceCharts = ({ incidents }) => {
    const siteData = useMemo(() => aggregateBySite(incidents).slice(0, 15), [incidents]);
    const timeData = useMemo(() => aggregateOverTime(incidents), [incidents]);
    const formData = useMemo(() => aggregateByFormType(incidents), [incidents]);

    return (
        <div className="occurrence-charts">
            {/* Left column — Occurrences by Site (full height) */}
            <div className="occurrence-charts__left">
                <div className="occurrence-summary__chart-panel occurrence-charts__site-panel">
                    <h4 className="occurrence-summary__chart-title">Occurrences by Site</h4>
                    <div className="occurrence-charts__site-scroll">
                        <div className="occurrence-summary__chart-container">
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart
                                    data={siteData}
                                    layout="vertical"
                                    margin={{ top: 5, right: 30, left: 10, bottom: 5 }}
                                >
                                    <CartesianGrid strokeDasharray="3 3" stroke={GRID_STROKE} horizontal={false} />
                                    <XAxis
                                        type="number"
                                        tick={TICK_STYLE}
                                        axisLine={{ stroke: AXIS_STROKE }}
                                        tickLine={false}
                                    />
                                    <YAxis
                                        type="category"
                                        dataKey="name"
                                        tick={{ ...TICK_STYLE, fontSize: 9 }}
                                        axisLine={false}
                                        tickLine={false}
                                        width={120}
                                    />
                                    <Tooltip content={<ChartTooltip />} />
                                    <Bar dataKey="count" name="Occurrences" fill={ACCENT} radius={[0, 4, 4, 0]} />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    </div>
                </div>
            </div>

            {/* Right column — Over Time (top) + Form Type (bottom) */}
            <div className="occurrence-charts__right">
                <div className="occurrence-summary__chart-panel occurrence-charts__time-panel">
                    <h4 className="occurrence-summary__chart-title">Occurrences Over Time</h4>
                    <div className="occurrence-summary__chart-container">
                        <ResponsiveContainer width="100%" height="100%">
                            <AreaChart
                                data={timeData}
                                margin={{ top: 5, right: 20, left: 10, bottom: 5 }}
                            >
                                <defs>
                                    <linearGradient id="occurrenceGradient" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%" stopColor={ACCENT} stopOpacity={0.3} />
                                        <stop offset="95%" stopColor={ACCENT} stopOpacity={0} />
                                    </linearGradient>
                                </defs>
                                <CartesianGrid strokeDasharray="3 3" stroke={GRID_STROKE} />
                                <XAxis
                                    dataKey="date"
                                    tick={TICK_STYLE}
                                    axisLine={{ stroke: AXIS_STROKE }}
                                    tickLine={false}
                                    tickFormatter={(val) => {
                                        const parts = val.split('-');
                                        return `${parts[2]}/${parts[1]}`;
                                    }}
                                />
                                <YAxis
                                    tick={TICK_STYLE}
                                    axisLine={false}
                                    tickLine={false}
                                    width={35}
                                />
                                <Tooltip content={<ChartTooltip isDate />} />
                                <Area
                                    type="monotone"
                                    dataKey="count"
                                    name="Occurrences"
                                    stroke={ACCENT}
                                    strokeWidth={2}
                                    fill="url(#occurrenceGradient)"
                                />
                            </AreaChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                <div className="occurrence-summary__chart-panel occurrence-charts__form-panel">
                    <h4 className="occurrence-summary__chart-title">Occurrences by Form Type</h4>
                    <div className="occurrence-summary__chart-container">
                        <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                                <Pie
                                    data={formData}
                                    cx="50%"
                                    cy="50%"
                                    innerRadius={50}
                                    outerRadius={85}
                                    paddingAngle={2}
                                    dataKey="count"
                                    nameKey="name"
                                    label={PieLabel}
                                    labelLine={false}
                                >
                                    {formData.map((entry, i) => (
                                        <Cell key={i} fill={entry.colour} />
                                    ))}
                                </Pie>
                                <Tooltip
                                    contentStyle={{
                                        background: 'rgba(20,10,40,0.95)',
                                        border: '1px solid rgba(255,255,255,0.2)',
                                        borderRadius: '6px',
                                        color: 'white',
                                        fontSize: '12px',
                                    }}
                                />
                                <Legend content={renderLegend} />
                            </PieChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            </div>
        </div>
    );
};

OccurrenceCharts.propTypes = {
    incidents: PropTypes.array.isRequired,
};

export default OccurrenceCharts;
